import {
  DESK_BLOCKS,
  ZERO,
  businessDate,
  fromColumn,
  sum,
  type BusinessDate,
  type DeskBand,
  type DeskBlock,
  type Money,
} from '@evogard/domain';
import type { DeskDashboard, DeskTally } from '@evogard/contract';

/**
 * O aproveitamento das bases somado — a API responde uma base por vez, e o
 * Kanban sempre mostrou as duas juntas.
 *
 * A mesma pessoa tem o mesmo `userId` nas duas bases (a identidade é da casa),
 * então somar é juntar pela chave. Os toques também somam sem contar duas
 * vezes: o card é de uma base só, e o mesmo card nunca aparece nas duas.
 */

export interface Tally {
  count: number;
  amount: Money;
}

export type LoadColumn = DeskBand | 'receptiva';

export interface MergedPerson {
  userId: string;
  name: string;
  listed: boolean;
  activeFlow: boolean;
  receptiveFlow: boolean;
  paidCollection: Tally;
  paidAutomatic: Tally;
  receptive: Tally;
  blocks: Record<DeskBlock, Tally>;
  touched: number;
  paidTouched: number;
  pulled: number;
  created: number;
  transfers: Map<string | null, number>;
  workload: Map<LoadColumn, Tally>;
}

export interface MergedDashboard {
  people: MergedPerson[];
  unattributed: { paidCollection: Tally; paidAutomatic: Tally };
  recovery: Array<{ day: BusinessDate; count: number; amount: Money }>;
  distribution: Map<LoadColumn, { queue: Tally; owned: Tally }>;
  /** As bases em que o rastro de movimentos começa depois do início do período, ou ainda não começou. */
  trailGaps: Array<{ tenant: string; since: BusinessDate | null }>;
}

const empty = (): Tally => ({ count: 0, amount: ZERO });
const of = (t: DeskTally): Tally => ({ count: t.count, amount: fromColumn(t.amount) ?? ZERO });
const add = (a: Tally, b: Tally): Tally => ({ count: a.count + b.count, amount: sum(a.amount, b.amount) });

export function mergeDashboards(parts: DeskDashboard[]): MergedDashboard {
  const people = new Map<string, MergedPerson>();
  const unattributed = { paidCollection: empty(), paidAutomatic: empty() };
  const recovery = new Map<BusinessDate, Tally>();
  const distribution = new Map<LoadColumn, { queue: Tally; owned: Tally }>();
  const trailGaps: MergedDashboard['trailGaps'] = [];

  for (const part of parts) {
    if (part.trailSince === null || part.trailSince > part.from) {
      trailGaps.push({ tenant: part.tenant.name, since: part.trailSince });
    }
    unattributed.paidCollection = add(unattributed.paidCollection, of(part.unattributed.paidCollection));
    unattributed.paidAutomatic = add(unattributed.paidAutomatic, of(part.unattributed.paidAutomatic));
    for (const r of part.recovery) recovery.set(r.day, add(recovery.get(r.day) ?? empty(), of(r)));
    for (const d of part.distribution) {
      const current = distribution.get(d.column) ?? { queue: empty(), owned: empty() };
      distribution.set(d.column, { queue: add(current.queue, of(d.queue)), owned: add(current.owned, of(d.owned)) });
    }

    for (const p of part.people) {
      const m: MergedPerson = people.get(p.userId) ?? {
        userId: p.userId,
        name: p.name,
        listed: false,
        activeFlow: false,
        receptiveFlow: false,
        paidCollection: empty(),
        paidAutomatic: empty(),
        receptive: empty(),
        blocks: Object.fromEntries(DESK_BLOCKS.map((b) => [b, empty()])) as Record<DeskBlock, Tally>,
        touched: 0,
        paidTouched: 0,
        pulled: 0,
        created: 0,
        transfers: new Map(),
        workload: new Map(),
      };
      m.listed ||= p.listed;
      m.activeFlow ||= p.activeFlow;
      m.receptiveFlow ||= p.receptiveFlow;
      m.paidCollection = add(m.paidCollection, of(p.paidCollection));
      m.paidAutomatic = add(m.paidAutomatic, of(p.paidAutomatic));
      m.receptive = add(m.receptive, of(p.receptive));
      for (const b of DESK_BLOCKS) m.blocks[b] = add(m.blocks[b], of(p.blocks[b]));
      m.touched += p.touched;
      m.paidTouched += p.paidTouched;
      m.pulled += p.pulled;
      m.created += p.created;
      for (const t of p.transfersReceived) {
        m.transfers.set(t.fromUserId, (m.transfers.get(t.fromUserId) ?? 0) + t.count);
      }
      for (const w of p.workload) m.workload.set(w.column, add(m.workload.get(w.column) ?? empty(), of(w)));
      people.set(p.userId, m);
    }
  }

  return {
    people: [...people.values()].sort(
      (a, b) =>
        b.paidCollection.count - a.paidCollection.count || a.name.localeCompare(b.name, 'pt-BR'),
    ),
    unattributed,
    recovery: [...recovery]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, t]) => ({ day, ...t })),
    distribution,
    trailGaps,
  };
}

/* ------------------------------------------------------------------------- *
 * O período — Dia, Semana, Mês e Intervalo, como o legado (`App.tsx:2437`),
 * mas no fuso da casa e com a semana e o mês escolhíveis
 * ------------------------------------------------------------------------- */

export type PeriodKind = 'dia' | 'semana' | 'mes' | 'intervalo';

export function today(): BusinessDate {
  return businessDate(new Date());
}

function shift(date: BusinessDate, days: number): BusinessDate {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** De segunda a domingo, a semana que contém o dia. */
export function weekOf(date: BusinessDate): { from: BusinessDate; to: BusinessDate } {
  const weekday = new Date(`${date}T12:00:00Z`).getUTCDay(); // 0 = domingo
  const from = shift(date, -((weekday + 6) % 7));
  return { from, to: shift(from, 6) };
}

/** `AAAA-MM` → do dia 1 ao último dia do mês. */
export function monthRange(month: string): { from: BusinessDate; to: BusinessDate } {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}

/** Os doze meses até o corrente, do mais novo para o mais velho. */
export function recentMonths(): string[] {
  const [y, m] = today().split('-').map(Number) as [number, number];
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(Date.UTC(y, m - 1 - i, 1));
    return d.toISOString().slice(0, 7);
  });
}

export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1, 15)).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
